import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-light-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import Portal from "elements/Portal";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import "components/select/style.scss";
import "./timeSelect.scss";

type Props = {
  value?: string;
  onChange: (time: string | undefined) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Minute interval between list options. Defaults to 15. Typed times are not forced to this step. */
  step?: number;
  /** Render dropdown in a portal (needed inside modals). */
  usePortal?: boolean;
  error?: boolean;
  helpText?: string;
};

const TIME_PARSE_FORMATS = [
  "HH:mm",
  "H:mm",
  "h:mm A",
  "hh:mm A",
  "h:mmA",
  "hh:mmA",
  "h:mm a",
  "hh:mm a",
  "h:mma",
  "hh:mma",
  "h A",
  "hA",
  "ha",
  "H",
  "HH",
];

function normalizeStep(step?: number): number {
  if (!step || !Number.isFinite(step) || step <= 0) {
    return 15;
  }
  return Math.min(60, Math.max(1, Math.floor(step)));
}

function buildTimeOptions(step: number): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const value = moment().startOf("day").add(minutes, "minutes").format("HH:mm");
    const label = moment(value, "HH:mm").format("h:mm A");
    options.push({ value, label });
  }
  return options;
}

function formatDisplay(value: string | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = moment(value, ["HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed.format("h:mm A") : value;
}

function parseFlexibleTime(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = moment(trimmed, TIME_PARSE_FORMATS, true);
  if (!parsed.isValid()) {
    return undefined;
  }

  return parsed.format("HH:mm");
}

/**
 * Time combobox: pick from step intervals or type a time freely.
 * Value is 24-hour `HH:mm`; display uses 12-hour format.
 */
export default function TimeSelect(props: Props) {
  const {
    value,
    onChange,
    label,
    placeholder = "Select a time",
    disabled,
    step: stepProp,
    usePortal = false,
    error,
    helpText,
  } = props;

  const step = normalizeStep(stepProp);
  const options = useMemo(() => buildTimeOptions(step), [step]);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => formatDisplay(value));
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
    openAbove: boolean;
  }>({ left: 0, width: 0, maxHeight: 280, openAbove: false });

  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const closeAndBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const commitDraft = useCallback(
    (nextDraft: string = draftRef.current) => {
      const trimmed = nextDraft.trim();
      if (!trimmed) {
        onChange(undefined);
        setDraft("");
        return;
      }

      const parsed = parseFlexibleTime(trimmed);
      if (parsed) {
        onChange(parsed);
        setDraft(formatDisplay(parsed));
        return;
      }

      setDraft(formatDisplay(value));
    },
    [onChange, value],
  );

  // Pass stable refs via options — do NOT call the hook's callback ref from
  // inline ref callbacks (it appends to state on every invocation → update loop).
  useOnclickOutside(
    () => {
      if (open) {
        commitDraft();
        closeAndBlur();
      }
    },
    { refs: [triggerRef, listRef] },
  );

  useEffect(() => {
    setDraft(formatDisplay(value));
  }, [value]);

  const filteredOptions = useMemo(() => {
    const query = draft.trim().toLowerCase();
    const currentLabel = formatDisplay(value).toLowerCase();
    // Don't filter to the committed value — show the full list until the user types.
    if (!query || !open || query === currentLabel) {
      return options;
    }

    const compactQuery = query.replace(/\s/g, "");
    return options.filter(option => {
      const compactLabel = option.label.replace(/\s/g, "").toLowerCase();
      return (
        option.label.toLowerCase().includes(query) ||
        option.value.includes(query) ||
        compactLabel.includes(compactQuery)
      );
    });
  }, [draft, open, options, value]);

  const updateDropdownPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const gap = 8;
    const preferredMax = 280;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openAbove = spaceBelow < preferredMax && spaceAbove > spaceBelow;
    const maxHeight = Math.max(Math.min(preferredMax, openAbove ? spaceAbove : spaceBelow), 80);

    if (openAbove) {
      setDropdownPosition({
        top: undefined,
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openAbove: true,
      });
    } else {
      setDropdownPosition({
        top: rect.bottom + gap,
        bottom: undefined,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openAbove: false,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updateDropdownPosition();
  }, [open, updateDropdownPosition, filteredOptions.length]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handle = () => updateDropdownPosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open || !value || !listRef.current) {
      return;
    }
    const selected = listRef.current.querySelector<HTMLElement>('[data-selected="true"]');
    selected?.scrollIntoView({ block: "nearest" });
  }, [open, value]);

  function handleSelectOption(optionValue: string) {
    const display = formatDisplay(optionValue);
    draftRef.current = display;
    setDraft(display);
    onChange(optionValue);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (event.key === "Escape") {
      setDraft(formatDisplay(value));
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (event.key === "ArrowDown") {
      setOpen(true);
    }
  }

  const dropdown = open && !disabled && (
    <ul
      ref={listRef}
      className={classNames(
        "rc-select-dropdown rc-select-dropdown-medium time-select__dropdown",
        { "time-select__dropdown--portal": usePortal },
      )}
      role="listbox"
      onMouseDown={event => event.preventDefault()}
      style={
        usePortal
          ? {
              position: "fixed",
              // Override `.rc-select-dropdown { top: 38px }` — omitting top left CSS in place
              // with bottom, which pinned the list near the viewport top instead of the trigger.
              ...(dropdownPosition.openAbove
                ? { top: "auto", bottom: dropdownPosition.bottom }
                : { top: dropdownPosition.top, bottom: "auto" }),
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight,
              zIndex: 115,
              marginTop: 0,
              minWidth: "fit-content",
            }
          : {
              maxHeight: dropdownPosition.maxHeight,
            }
      }
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map(option => (
          <li
            key={option.value}
            role="option"
            aria-selected={option.value === value}
            data-selected={option.value === value ? "true" : undefined}
            className={classNames("rc-select-dropdown-list-item", {
              "rc-select-dropdown-list-item-selected": option.value === value,
            })}
            onMouseDown={event => {
              event.preventDefault();
              handleSelectOption(option.value);
            }}
          >
            {option.label}
          </li>
        ))
      ) : (
        <li className="rc-select-dropdown-list-item time-select__empty">No matching times</li>
      )}
    </ul>
  );

  return (
    <div className="time-select rc-select-container">
      {label ? (
        <div className="rc-select-dropdown-label">
          <label>{label}</label>
        </div>
      ) : null}

      <div
        ref={triggerRef}
        className={classNames("rc-select rc-select-hasSearch time-select__trigger", {
          "rc-select-disabled": disabled,
          "rc-select-error": error,
        })}
        onClick={() => {
          if (disabled) {
            return;
          }
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        <FontAwesomeIcon className="rc-select-dropdown-icon" icon={faClock} />
        <input
          ref={inputRef}
          type="text"
          className={classNames("rc-select-search text-black font-medium w-full", {
            "rc-select-search_disabled": disabled,
          })}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={event => {
            setDraft(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) {
              setOpen(true);
            }
          }}
          onBlur={() => {
            commitDraft(draftRef.current);
            setOpen(false);
          }}
          onKeyDown={handleKeyDown}
        />
        <FontAwesomeIcon className="rc-select-arrow" icon={faChevronDown} />
      </div>

      {usePortal ? <Portal>{dropdown}</Portal> : dropdown}

      {helpText ? (
        <div className="rc-select-help-text font-normal text-gray-400 mt-2">
          <span>{helpText}</span>
        </div>
      ) : null}
    </div>
  );
}
